// The director as a music source (@rpg/contracts' MusicSource; decision 0078): an adapter, so an adaptive-music
// feature drives any source through one contract and this director is the first implementation. The director keeps
// its own API; this maps the contract onto it:
//   moods          the table's moods, each landing on the next bar line (the director's `mood`)
//   states         explore (no combat layer), combat (the combat layer, entering on the bar: `alert(true)`),
//                  victory (the victory phrase on the next bar line: `victory`)
//   intensity      stepped: while exploring, at or above `combatAt` (default 0.75) the combat layer enters as if
//                  alert; below it, it leaves after the table's hold
//   transitions    on the bar only (the director lands everything on bar lines)
// The director may be scheduled by someone else (the audio engine updates its own director every frame): then the
// feature calls only `setMood`, `setState` and `setIntensity` with the engine's clock (`Audio.now()`), never `start` or
// `update`, and `grid` and `state` still report the director's grid.
import { clampIntensity, MUSIC_SOURCE_VERSION, type BarGrid, type MusicSource, type MusicStateName } from "@rpg/contracts";
import type { MusicDirector, MusicTable } from "./music";

/** Wrap a director (built over `table`) as a music source. */
export function directorMusicSource(director: MusicDirector, table: MusicTable, o: { combatAt?: number } = {}): MusicSource {
  const combatAt = o.combatAt ?? 0.75;
  const moods = table.moods.map((m) => m.id);
  const states: MusicStateName[] = ["explore", "combat", "victory"];
  let started = false;
  let state: MusicStateName = "explore";
  let intensity = 0;
  const layer = (now: number): void => director.alert(state === "combat" || (state === "explore" && intensity >= combatAt), now);
  // the grid is the director's own: a director something else schedules (the audio engine's, which starts it on its
  // first update once the instruments are ready) reports its grid here too, whether or not `start` was called
  const grid = (): BarGrid | null => {
    const s = director.state();
    if (s.nextBar === null) return null;
    const mood = table.moods.find((m) => m.id === s.mood);
    return { barLength: s.barLength, beatsPerBar: mood?.beatsPerBar ?? 4, nextBar: s.nextBar, bar: s.bar };
  };
  return {
    contract: MUSIC_SOURCE_VERSION,
    capabilities: () => ({ moods, states, transitions: ["bar"], intensity: "stepped" }),
    start(now) {
      director.start(now);
      started = true;
    },
    setMood(mood, now) {
      if (moods.includes(mood)) director.mood(mood, now);
    },
    setState(next, now) {
      if (!states.includes(next)) return;
      state = next;
      if (next === "victory") director.victory(now);
      else layer(now);
    },
    setIntensity(level, now) {
      intensity = clampIntensity(level);
      layer(now);
    },
    update(now) {
      if (started) director.update(now);
    },
    grid,
    state: () => ({ mood: director.state().mood, state, intensity, grid: grid() }),
  };
}
